import { DateTime, DurationUnit } from "luxon";

import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

class LuxonDateHelper {
    public static dateDiff(startDate: Date, endDate: Date, unit: DurationUnit, shouldConvertToUtc: boolean = false): number {
        if (!startDate || !endDate)
            return null;

        const monthsPerYear = 12;
        const daysPerMonth = 30.4167;
        const daysPerYear = monthsPerYear * daysPerMonth;
        const luxonStarDate = LuxonDateHelper.toMidnightAndOptionallyToUtc(startDate, shouldConvertToUtc);
        const luxonEndDate = LuxonDateHelper.toMidnightAndOptionallyToUtc(endDate, shouldConvertToUtc);

        switch (unit) {
            case "years":
                const yearsDiff = luxonEndDate.diff(luxonStarDate, ["years", "months", "days"]);
                return yearsDiff.years + (yearsDiff.months / monthsPerYear) + (yearsDiff.days / daysPerYear);
            case "months":
                const monthsDiff = luxonEndDate.diff(luxonStarDate, ["years", "months", "days"]);
                return monthsDiff.years * monthsPerYear + monthsDiff.months + (monthsDiff.days / daysPerMonth);
            case "weeks":
                const weeksDiff = luxonEndDate.diff(luxonStarDate, ["weeks"]);
                return weeksDiff.weeks;
            case "days":
                const daysDiff = luxonEndDate.diff(luxonStarDate, ["days"]);
                return daysDiff.days;
            default:
                return null;
        }
    }

    public static dateAdd(startDate: Date, duration: number, unit: DurationUnit, shouldConvertToUtc: boolean = false): Date {
        if (!startDate)
            return null;

        const luxonStarDate = LuxonDateHelper.toMidnightAndOptionallyToUtc(startDate, shouldConvertToUtc);

        switch (unit) {
            case "years":
                return luxonStarDate.plus({ years: duration }).toJSDate();
            case "months":
                return luxonStarDate.plus({ months: duration }).toJSDate();
            case "weeks":
                return luxonStarDate.plus({ weeks: duration }).toJSDate();
            default:
                return null;
        }
    }

    /**
     * Get the age in years from a date of birth.
     * @param dateOfBirth The date of birth.
     */
    public static getYearsSince(dateOfBirth: string | Date | DateTime): number {
        const parsed = LuxonDateTimeParser.parse(dateOfBirth);
        return parsed
            ? Math.floor(-parsed.diffNow("years").years)
            : null;
    }

    /**
     * Get a translated date in the form 'yyyy-MM-dd at HH:mm:ss', or an empty string if the date is empty.
     * @param date The date to format.
     */
    public static formatToFullDateTime(date: luxon.DateTime | Date | string | null, separator: string = "-"): string {
        if (!date)
            return "";

        const parsedDate = LuxonDateTimeParser.parse(date);
        const dateFormat = `yyyy${separator}MM${separator}dd`;
        return `${parsedDate.toFormat(dateFormat)} ${cacheStore.GetString("AtLabel")} ${parsedDate.toLocal().toLocaleString(DateTime.TIME_24_WITH_SECONDS)}`;
    }

    /**
     * Convert the provided date to midnight time, and optionally convert to UTC.
     */
    private static toMidnightAndOptionallyToUtc(date: Date, convertToUtc: boolean): DateTime {
        const dateAtMidnight = LuxonDateHelper.setToMidnight(date);

        if (convertToUtc)
            return dateAtMidnight.toUTC();

        return dateAtMidnight;
    }

    /**
     * Reset the time of the given date to midnight.
     */
    private static setToMidnight(date: Date): DateTime {
        const parsedDate = LuxonDateTimeParser
            .parse(date)
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

        return parsedDate;
    }
}

export { LuxonDateHelper };
